/**
 * ChatModerationCenter — Unified moderation dashboard for chat_archives
 * across ALL services (visa-id, company-formation, tax-agent, ...).
 *
 * Complements the existing per-service ChatArchivesMain/List pages by providing
 * a single hub with cross-service filters + bulk delete + review-in-place.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MessageSquare, Clock, CheckCircle2, Search, RefreshCw, Trash2,
  Loader2, Filter, AlertTriangle, User, Bot, Calendar,
  Globe2, Building2, FileText, Scale, DollarSign, Landmark, ChevronRight, X, Download,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SERVICE_META = {
  'visa-id':           { icon: Globe2,   labelAr: 'التأشيرات والهوية',   labelEn: 'Visa & ID',           color: 'text-cyan-300',    bg: 'bg-cyan-500/15',    border: 'border-cyan-500/40'    },
  'company-formation': { icon: Building2, labelAr: 'تأسيس الشركات',       labelEn: 'Company Formation',   color: 'text-emerald-300', bg: 'bg-emerald-500/15', border: 'border-emerald-500/40' },
  'printing-docs':     { icon: FileText, labelAr: 'الطباعة والوثائق',    labelEn: 'Printing & Docs',     color: 'text-amber-300',   bg: 'bg-amber-500/15',   border: 'border-amber-500/40'   },
  'legal-translation': { icon: Scale,    labelAr: 'الترجمة القانونية',   labelEn: 'Legal Translation',   color: 'text-blue-300',    bg: 'bg-blue-500/15',    border: 'border-blue-500/40'    },
  'bill-payment':      { icon: DollarSign, labelAr: 'دفع الفواتير',      labelEn: 'Bill Payment',        color: 'text-pink-300',    bg: 'bg-pink-500/15',    border: 'border-pink-500/40'    },
  'tax-agent':         { icon: Landmark, labelAr: 'الوكيل الضريبي',      labelEn: 'Tax Agent',           color: 'text-purple-300',  bg: 'bg-purple-500/15',  border: 'border-purple-500/40'  },
};

const fmtDate = (iso, lang) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(lang === 'ar' ? 'ar-AE' : 'en-AE', {
      year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

// -----------------------------------------------------------------------------
// Detail Dialog — view full chat + review + delete
// -----------------------------------------------------------------------------
function ArchiveDetailDialog({ open, onClose, archive, language, onReviewed }) {
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && archive) setNotes(archive.review_notes || '');
  }, [open, archive]);

  if (!archive) return null;

  const submit = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/chat-archives/${archive.id}/review`,
        { review_notes: notes },
        { headers: getAuthHeaders() },
      );
      toast.success(language === 'ar' ? 'تم حفظ المراجعة' : 'Review saved');
      onReviewed();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const svc = SERVICE_META[archive.service_type] || {};

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-3xl bg-[#09090b] border-white/10 text-white p-0 overflow-hidden"
        dir={language === 'ar' ? 'rtl' : 'ltr'}
        data-testid="mod-detail-dialog"
      >
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-white/10 bg-gradient-to-br from-[#d4af37]/10 to-transparent">
          <DialogTitle className="text-lg font-bold text-white flex items-center gap-2 flex-wrap">
            <MessageSquare className="h-5 w-5 text-[#d4af37]" />
            <span className="font-mono text-[#d4af37]">{archive.reference_number || archive.id?.slice(0, 12)}</span>
            {archive.review_status === 'reviewed' ? (
              <span className="text-[10px] font-bold text-emerald-300 bg-emerald-500/15 border border-emerald-500/40 px-1.5 py-0.5 rounded">
                {language === 'ar' ? 'تمت المراجعة' : 'Reviewed'}
              </span>
            ) : (
              <span className="text-[10px] font-bold text-amber-300 bg-amber-500/15 border border-amber-500/40 px-1.5 py-0.5 rounded">
                {language === 'ar' ? 'لم تتم المراجعة' : 'Not Reviewed'}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#A1A1AA] mt-1">
            {archive.customer_name || '—'} · {fmtDate(archive.created_at, language)} ·{' '}
            <span className={svc.color}>{language === 'ar' ? svc.labelAr : svc.labelEn}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-4 max-h-[55vh] overflow-y-auto space-y-4">
          {/* Meta: Platform + AI Provider + Model */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
            <div className="bg-black/30 border border-white/10 rounded-lg px-3 py-2">
              <div className="text-[9px] text-white/40 uppercase tracking-wider mb-0.5">
                {language === 'ar' ? 'المنصة' : 'Platform'}
              </div>
              <div className="text-cyan-300 font-semibold">
                {archive.platform || archive.source_platform || '—'}
              </div>
            </div>
            <div className="bg-black/30 border border-white/10 rounded-lg px-3 py-2">
              <div className="text-[9px] text-white/40 uppercase tracking-wider mb-0.5">
                {language === 'ar' ? 'اسم الـ Agent' : 'Agent Name'}
              </div>
              <div className={`font-semibold ${
                (archive.ai_provider || archive.provider) === 'anthropic'
                  ? 'text-orange-300'
                  : (archive.ai_provider || archive.provider) === 'google'
                    ? 'text-blue-300'
                    : 'text-white/50'
              }`}>
                {archive.agent_name
                  || archive.bot_name
                  || ((archive.ai_provider || archive.provider) === 'anthropic'
                    ? 'Claude'
                    : (archive.ai_provider || archive.provider) === 'google'
                      ? 'Gemini'
                      : (archive.ai_provider || archive.provider || '—'))}
              </div>
            </div>
            <div className="bg-black/30 border border-white/10 rounded-lg px-3 py-2">
              <div className="text-[9px] text-white/40 uppercase tracking-wider mb-0.5">
                {language === 'ar' ? 'الموديل' : 'Model'}
              </div>
              <div className="text-white/85 font-mono text-[10px] truncate">
                {archive.ai_model || archive.model || '—'}
              </div>
            </div>
            <div
              className="bg-black/30 border border-white/10 rounded-lg px-3 py-2"
              data-testid="mod-attachments-count"
            >
              <div className="text-[9px] text-white/40 uppercase tracking-wider mb-0.5">
                {language === 'ar' ? 'عدد المرفقات' : 'Attachments'}
              </div>
              <div className="text-white/85 font-semibold font-mono">
                {(archive.attachments?.length
                  || archive.documents_uploaded?.length
                  || archive.attachments_count
                  || 0)}
              </div>
            </div>
          </div>

          {/* Chat */}
          <div>
            <h3 className="text-xs font-bold text-white/80 mb-2 uppercase">
              {language === 'ar' ? `الرسائل (${archive.messages?.length || 0})` : `Messages (${archive.messages?.length || 0})`}
            </h3>
            <div className="space-y-2 max-h-80 overflow-y-auto pe-2 bg-black/20 border border-white/5 rounded-lg p-3">
              {(archive.messages || []).map((m, i) => {
                const isBot = m.role === 'bot' || m.role === 'assistant';
                return (
                  <div key={i} className={`flex gap-2 ${isBot ? '' : 'flex-row-reverse'}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${isBot ? 'bg-[#d4af37]/20 text-[#d4af37]' : 'bg-emerald-500/20 text-emerald-300'}`}>
                      {isBot ? <Bot className="h-3 w-3" /> : <User className="h-3 w-3" />}
                    </div>
                    <div className={`max-w-[75%] rounded-2xl px-2.5 py-1.5 text-xs ${isBot ? 'bg-white/5 border border-white/10 rounded-tl-sm' : 'bg-emerald-500/15 border border-emerald-500/30 rounded-tr-sm'}`}>
                      <div className="whitespace-pre-wrap break-words">{m.message || m.content}</div>
                      {m.timestamp && <div className="text-[9px] text-white/40 mt-0.5">{fmtDate(m.timestamp, language)}</div>}
                    </div>
                  </div>
                );
              })}
              {!archive.messages?.length && <div className="text-center text-xs text-white/40 py-4">{language === 'ar' ? 'لا رسائل' : 'No messages'}</div>}
            </div>
          </div>

          {/* Review notes */}
          <div>
            <h3 className="text-xs font-bold text-white/80 mb-1 uppercase">
              {language === 'ar' ? 'ملاحظات المراجعة' : 'Review Notes'}
            </h3>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder={language === 'ar' ? 'أضف ملاحظاتك (سيتم أرشفة المحادثة)...' : 'Add your notes (chat will be archived)...'}
              className="w-full bg-black/40 border border-white/10 rounded-md text-white text-sm p-2 resize-y"
              data-testid="mod-review-notes"
            />
            {archive.reviewer_name && archive.reviewed_at && (
              <div className="text-[10px] text-white/40 mt-1">
                {language === 'ar' ? 'المُراجِع:' : 'Reviewer:'} <b>{archive.reviewer_name}</b> · {fmtDate(archive.reviewed_at, language)}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-white/10 bg-black/40">
          <Button variant="ghost" onClick={onClose} className="text-white/70" data-testid="mod-detail-close">
            {language === 'ar' ? 'إغلاق' : 'Close'}
          </Button>
          <Button
            onClick={submit}
            disabled={saving || !notes.trim()}
            title={
              !notes.trim()
                ? (language === 'ar' ? 'أضف ملاحظات المراجعة أولاً' : 'Add review notes first')
                : ''
            }
            className="bg-gradient-to-r from-[#d4af37] to-[#9b7d20] text-black font-bold disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="mod-review-save"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin me-1.5" /> : <CheckCircle2 className="h-4 w-4 me-1.5" />}
            {language === 'ar' ? 'حفظ المراجعة وأرشفة' : 'Save & Archive'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Main page
// -----------------------------------------------------------------------------
export default function ChatModerationCenter({ reviewedOnly = false }) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(reviewedOnly ? 'reviewed' : 'new');
  const [serviceFilter, setServiceFilter] = useState('all');
  // Extra filters added per user request (iter-114): filter the archive
  // list by originating platform (asb606 / cloudgm_customer / cloudgm_business)
  // and by AI provider (anthropic → Claude, google → Gemini).
  const [platformFilter, setPlatformFilter] = useState('all');
  const [providerFilter, setProviderFilter] = useState('all');
  const [viewing, setViewing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [exporting, setExporting] = useState(false);

  const exportCsv = useCallback(async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      const effectiveStatus = reviewedOnly ? 'reviewed' : statusFilter;
      if (effectiveStatus !== 'all') params.append('review_status', effectiveStatus);
      if (serviceFilter !== 'all')   params.append('service_type', serviceFilter);
      if (platformFilter !== 'all')  params.append('platform', platformFilter);
      if (providerFilter !== 'all')  params.append('provider', providerFilter);

      const res = await axios.get(
        `${API_URL}/api/admin/chat-archives/export.csv?${params}`,
        { headers: getAuthHeaders(), responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `chat-archives-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      const rows = res.headers?.['x-row-count'] || '?';
      toast.success(
        language === 'ar'
          ? `✅ تمّ تصدير ${rows} محادثة إلى CSV`
          : `✅ Exported ${rows} conversations to CSV`,
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل التصدير' : 'Export failed'));
    } finally {
      setExporting(false);
    }
  }, [reviewedOnly, statusFilter, serviceFilter, platformFilter, providerFilter, language]);

  const exportXlsx = useCallback(async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      const effectiveStatus = reviewedOnly ? 'reviewed' : statusFilter;
      if (effectiveStatus !== 'all') params.append('review_status', effectiveStatus);
      if (serviceFilter !== 'all')   params.append('service_type', serviceFilter);
      if (platformFilter !== 'all')  params.append('platform', platformFilter);
      if (providerFilter !== 'all')  params.append('provider', providerFilter);

      const res = await axios.get(
        `${API_URL}/api/admin/chat-archives/export.xlsx?${params}`,
        { headers: getAuthHeaders(), responseType: 'blob' },
      );
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `chat-archives-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      const rows = res.headers?.['x-row-count'] || '?';
      const plats = res.headers?.['x-platform-count'] || '?';
      toast.success(
        language === 'ar'
          ? `✅ تمّ تصدير ${rows} محادثة في ${plats} تبويب`
          : `✅ Exported ${rows} conversations across ${plats} sheets`,
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل التصدير' : 'Export failed'));
    } finally {
      setExporting(false);
    }
  }, [reviewedOnly, statusFilter, serviceFilter, platformFilter, providerFilter, language]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch across all services by NOT passing service_type
      const params = new URLSearchParams();
      // In reviewed-only mode, always constrain to reviewed status.
      const effectiveStatus = reviewedOnly ? 'reviewed' : statusFilter;
      if (effectiveStatus !== 'all') params.append('review_status', effectiveStatus);
      const res = await axios.get(`${API_URL}/api/admin/chat-archives?${params}`, { headers: getAuthHeaders() });
      setArchives(res.data?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل التحميل' : 'Failed to load'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, language, reviewedOnly]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return archives.filter((a) => {
      if (serviceFilter !== 'all' && a.service_type !== serviceFilter) return false;
      // Platform filter — accepts either the "platform" or "source_platform"
      // field so we tolerate both spellings from the various producers
      // (ASB606 admin actions, CloudGM Customer, CloudGM Business).
      if (platformFilter !== 'all') {
        const p = (a.platform || a.source_platform || '').toString().toLowerCase();
        if (p !== platformFilter) return false;
      }
      // Provider filter — the raw value from the DB is `anthropic` or
      // `google`; the chips show Claude / Gemini for readability.
      if (providerFilter !== 'all') {
        const prov = (a.ai_provider || a.provider || '').toString().toLowerCase();
        if (prov !== providerFilter) return false;
      }
      if (q) {
        const hay = [a.reference_number, a.customer_name, a.customer_email, a.review_notes].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [archives, search, serviceFilter, platformFilter, providerFilter]);

  const stats = useMemo(() => {
    const byService = {};
    const byPlatform = {};
    const byProvider = {};
    archives.forEach((a) => {
      byService[a.service_type] = (byService[a.service_type] || 0) + 1;
      const p = (a.platform || a.source_platform || '').toString().toLowerCase();
      if (p) byPlatform[p] = (byPlatform[p] || 0) + 1;
      const prov = (a.ai_provider || a.provider || '').toString().toLowerCase();
      if (prov) byProvider[prov] = (byProvider[prov] || 0) + 1;
    });
    return {
      total: archives.length,
      new: archives.filter((a) => a.review_status === 'new').length,
      reviewed: archives.filter((a) => a.review_status === 'reviewed').length,
      byService,
      byPlatform,
      byProvider,
    };
  }, [archives]);

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/api/admin/chat-archives/${deleteTarget.id}`, { headers: getAuthHeaders() });
      toast.success(language === 'ar' ? 'تم الحذف' : 'Deleted');
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  return (
    <DashboardLayout
      title={
        reviewedOnly
          ? (language === 'ar' ? 'أرشيف محادثات AI' : 'AI Chat Archives')
          : (language === 'ar' ? 'محادثات الذكاء الاصطناعي' : 'AI Conversations')
      }
      subtitle={
        reviewedOnly
          ? (language === 'ar' ? 'المحادثات التي تمّت مراجعتها وأرشفتها' : 'Chats that have been reviewed & archived')
          : (language === 'ar' ? 'مراجعة كل محادثات الذكاء الاصطناعي من جميع المنصات في مكان واحد' : 'Review all AI conversations across every platform in one hub')
      }
      screenCode={reviewedOnly ? 'ARC-AI-001' : 'MOD-001'}
      breadcrumb={
        reviewedOnly
          ? [
              { labelAr: 'لوحة التحكم', labelEn: 'Dashboard', path: '/dashboard' },
              { labelAr: 'الأرشيف', labelEn: 'Archive', path: '/control/archive' },
              { labelAr: 'محادثات AI', labelEn: 'AI Chats', path: null },
            ]
          : [
              { labelAr: 'لوحة التحكم', labelEn: 'Dashboard', path: '/dashboard' },
              { labelAr: 'التحكم بالـ AI', labelEn: 'AI Control', path: '/control/ai' },
              { labelAr: 'محادثات الذكاء الاصطناعي', labelEn: 'AI Conversations', path: null },
            ]
      }
    >
      <section className="max-w-7xl mx-auto space-y-5" data-testid={reviewedOnly ? 'ai-chat-archive-page' : 'chat-moderation-page'}>
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-gradient-to-br from-[#d4af37]/15 to-transparent border border-[#d4af37]/30 rounded-2xl p-4">
            <div className="flex items-center justify-between"><MessageSquare className="h-5 w-5 text-[#d4af37]" /></div>
            <div className="text-2xl font-bold text-white mt-2">{stats.total}</div>
            <div className="text-[11px] text-white/60">{language === 'ar' ? 'الإجمالي' : 'Total'}</div>
          </div>
          {!reviewedOnly && (
            <div className="bg-gradient-to-br from-amber-500/15 to-transparent border border-amber-500/30 rounded-2xl p-4">
              <div className="flex items-center justify-between"><Clock className="h-5 w-5 text-amber-300" /></div>
              <div className="text-2xl font-bold text-white mt-2">{stats.new}</div>
              <div className="text-[11px] text-white/60">{language === 'ar' ? 'بانتظار المراجعة' : 'Awaiting Review'}</div>
            </div>
          )}
          {!reviewedOnly ? null : (
            <div className="bg-gradient-to-br from-emerald-500/15 to-transparent border border-emerald-500/30 rounded-2xl p-4">
              <div className="flex items-center justify-between"><CheckCircle2 className="h-5 w-5 text-emerald-300" /></div>
              <div className="text-2xl font-bold text-white mt-2">{stats.reviewed}</div>
              <div className="text-[11px] text-white/60">{language === 'ar' ? 'تمت المراجعة' : 'Reviewed'}</div>
            </div>
          )}
          <div className="bg-gradient-to-br from-blue-500/15 to-transparent border border-blue-500/30 rounded-2xl p-4">
            <div className="flex items-center justify-between"><Filter className="h-5 w-5 text-blue-300" /></div>
            <div className="text-2xl font-bold text-white mt-2">{Object.keys(stats.byService).length}</div>
            <div className="text-[11px] text-white/60">{language === 'ar' ? 'خدمات نشطة' : 'Active Services'}</div>
          </div>
        </div>

        {/* Platform + Provider filter chips — iter-114
            These sit above the service chips so the trader can slice by
            SOURCE first (which app produced the chat + which AI model
            answered), then by internal service type. */}
        {(Object.keys(stats.byPlatform).length > 0 || Object.keys(stats.byProvider).length > 0) && (
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-3 space-y-2">
            {Object.keys(stats.byPlatform).length > 0 && (
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-[10px] text-white/50 uppercase tracking-wider me-1">
                  {language === 'ar' ? 'المنصة:' : 'Platform:'}
                </span>
                <button
                  onClick={() => setPlatformFilter('all')}
                  className={`text-[11px] px-2 py-1 rounded-md border transition-all ${
                    platformFilter === 'all'
                      ? 'bg-[#d4af37]/15 border-[#d4af37]/40 text-[#d4af37] ring-1 ring-white/20'
                      : 'bg-black/40 border-white/10 text-white/70 hover:border-white/25'
                  }`}
                  data-testid="mod-platform-chip-all"
                >
                  {language === 'ar' ? 'الكل' : 'All'}
                  <span className="ms-1 font-mono font-bold">{stats.total}</span>
                </button>
                {Object.entries(stats.byPlatform).map(([p, count]) => {
                  const labelMap = {
                    asb606: { ar: 'ASB606', en: 'ASB606', color: 'bg-purple-500/10 border-purple-500/30 text-purple-300' },
                    cloudgm_customer: { ar: 'CloudGM كستمر', en: 'CloudGM Customer', color: 'bg-cyan-500/10 border-cyan-500/30 text-cyan-300' },
                    cloudgm_business: { ar: 'CloudGM بزنس', en: 'CloudGM Business', color: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' },
                  };
                  const meta = labelMap[p] || {
                    ar: p, en: p, color: 'bg-white/5 border-white/15 text-white/70',
                  };
                  const active = platformFilter === p;
                  return (
                    <button
                      key={p}
                      onClick={() => setPlatformFilter(active ? 'all' : p)}
                      className={`text-[11px] px-2 py-1 rounded-md border transition-all ${
                        active ? `${meta.color} ring-1 ring-white/30` : 'bg-black/40 border-white/10 text-white/70 hover:border-white/25'
                      }`}
                      data-testid={`mod-platform-chip-${p}`}
                    >
                      {language === 'ar' ? meta.ar : meta.en}
                      <span className="ms-1 font-mono font-bold">{count}</span>
                    </button>
                  );
                })}
              </div>
            )}
            {Object.keys(stats.byProvider).length > 0 && (
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-[10px] text-white/50 uppercase tracking-wider me-1">
                  {language === 'ar' ? 'الذكاء الاصطناعي:' : 'AI:'}
                </span>
                <button
                  onClick={() => setProviderFilter('all')}
                  className={`text-[11px] px-2 py-1 rounded-md border transition-all ${
                    providerFilter === 'all'
                      ? 'bg-[#d4af37]/15 border-[#d4af37]/40 text-[#d4af37] ring-1 ring-white/20'
                      : 'bg-black/40 border-white/10 text-white/70 hover:border-white/25'
                  }`}
                  data-testid="mod-provider-chip-all"
                >
                  {language === 'ar' ? 'الكل' : 'All'}
                </button>
                {Object.entries(stats.byProvider).map(([prov, count]) => {
                  const isClaude = prov === 'anthropic';
                  const isGemini = prov === 'google';
                  const label = isClaude ? 'Claude' : isGemini ? 'Gemini' : prov;
                  const color = isClaude
                    ? 'bg-orange-500/10 border-orange-500/30 text-orange-300'
                    : isGemini
                      ? 'bg-blue-500/10 border-blue-500/30 text-blue-300'
                      : 'bg-white/5 border-white/15 text-white/70';
                  const active = providerFilter === prov;
                  return (
                    <button
                      key={prov}
                      onClick={() => setProviderFilter(active ? 'all' : prov)}
                      className={`text-[11px] px-2 py-1 rounded-md border transition-all ${
                        active ? `${color} ring-1 ring-white/30` : 'bg-black/40 border-white/10 text-white/70 hover:border-white/25'
                      }`}
                      data-testid={`mod-provider-chip-${prov}`}
                    >
                      {label}
                      <span className="ms-1 font-mono font-bold">{count}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Service breakdown chips */}
        {Object.keys(stats.byService).length > 0 && (
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-3 flex flex-wrap gap-2 items-center">
            <span className="text-[10px] text-white/50 uppercase tracking-wider me-1">{language === 'ar' ? 'حسب الخدمة:' : 'By service:'}</span>
            {Object.entries(stats.byService).map(([svc, count]) => {
              const meta = SERVICE_META[svc] || {};
              const Icon = meta.icon || MessageSquare;
              return (
                <button
                  key={svc}
                  onClick={() => setServiceFilter(serviceFilter === svc ? 'all' : svc)}
                  className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border transition-all ${
                    serviceFilter === svc ? `${meta.bg} ${meta.color} ${meta.border} ring-1 ring-white/30` : `bg-black/40 border-white/10 text-white/70 hover:border-white/25`
                  }`}
                  data-testid={`mod-service-chip-${svc}`}
                >
                  <Icon className="h-3 w-3" />
                  {language === 'ar' ? meta.labelAr : meta.labelEn}
                  <span className="ms-1 font-mono font-bold">{count}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Toolbar */}
        <div className="flex flex-col md:flex-row gap-2">
          <div className="relative flex-1">
            <Search className={`h-4 w-4 text-white/40 absolute top-1/2 -translate-y-1/2 ${language === 'ar' ? 'right-3' : 'left-3'}`} />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={language === 'ar' ? 'ابحث برقم المرجع / الاسم / البريد / الملاحظات...' : 'Search by ref / name / email / notes...'}
              className={`bg-black/40 border-white/10 text-white ${language === 'ar' ? 'pr-9' : 'pl-9'}`}
              data-testid="mod-search"
            />
          </div>
          <div className="flex items-center gap-1 bg-black/40 border border-white/10 rounded-lg p-1">
            {(reviewedOnly ? ['reviewed'] : ['new']).map((s) => (
              <button
                key={s}
                onClick={() => !reviewedOnly && setStatusFilter(s)}
                disabled={reviewedOnly}
                className={`px-3 py-1.5 text-xs rounded-md ${statusFilter === s ? 'bg-[#d4af37] text-black font-bold' : 'text-white/60 hover:text-white'} ${reviewedOnly ? 'cursor-default' : ''}`}
                data-testid={`mod-status-${s}`}
              >
                {language === 'ar'
                  ? { new: 'لم يتم المراجعة', reviewed: 'تم المراجعة' }[s]
                  : { new: 'Not Reviewed', reviewed: 'Reviewed' }[s]}
              </button>
            ))}
          </div>
          <button
            onClick={exportCsv}
            disabled={exporting || filtered.length === 0}
            className="px-3 py-2 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 flex items-center gap-1.5 text-xs font-bold transition disabled:opacity-40 disabled:cursor-not-allowed"
            title={language === 'ar' ? 'تصدير المرشّحات الحاليّة إلى CSV' : 'Export current filters to CSV'}
            data-testid="mod-export-csv"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            <span className="hidden sm:inline">
              {language === 'ar' ? 'CSV' : 'CSV'}
            </span>
          </button>
          <button
            onClick={exportXlsx}
            disabled={exporting || filtered.length === 0}
            className="px-3 py-2 rounded-lg bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 text-blue-300 flex items-center gap-1.5 text-xs font-bold transition disabled:opacity-40 disabled:cursor-not-allowed"
            title={language === 'ar' ? 'تصدير إلى Excel (تبويب لكل منصّة)' : 'Export to Excel (one sheet per platform)'}
            data-testid="mod-export-xlsx"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            <span className="hidden sm:inline">
              {language === 'ar' ? 'XLSX' : 'XLSX'}
            </span>
          </button>
          <button
            onClick={load}
            className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/70"
            title={language === 'ar' ? 'تحديث' : 'Refresh'}
            data-testid="mod-refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>

        {/* Table */}
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-14">
              <Loader2 className="h-8 w-8 animate-spin text-[#d4af37]" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <MessageSquare className="h-12 w-12 mx-auto text-white/20 mb-2" />
              <h3 className="text-sm font-bold text-white">
                {language === 'ar' ? 'لا توجد محادثات مطابقة' : 'No matching archives'}
              </h3>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-white/60 text-[11px] uppercase">
                  <tr>
                    <th className="text-start px-3 py-3">{language === 'ar' ? 'المرجع' : 'Ref'}</th>
                    <th className="text-start px-3 py-3">{language === 'ar' ? 'العميل' : 'Customer'}</th>
                    <th className="text-start px-3 py-3">{language === 'ar' ? 'المنصة' : 'Platform'}</th>
                    {/* iter-232b — user request: "Service" column replaced by "Bot Name". */}
                    <th className="text-start px-3 py-3">{language === 'ar' ? 'مسمى البوت' : 'Bot name'}</th>
                    {/* iter-232b — user request: "Model" renamed to "Model used", shows Gemini/Claude. */}
                    <th className="text-start px-3 py-3">{language === 'ar' ? 'النموذج المستخدم' : 'Model used'}</th>
                    <th className="text-start px-3 py-3">{language === 'ar' ? 'الرسائل' : 'Msgs'}</th>
                    <th className="text-start px-3 py-3">{language === 'ar' ? 'التاريخ' : 'Date'}</th>
                    <th className="text-start px-3 py-3">{language === 'ar' ? 'الحالة' : 'Status'}</th>
                    <th className="px-3 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((a) => {
                    const svc = SERVICE_META[a.service_type] || {};
                    const SvcIcon = svc.icon || MessageSquare;
                    const platformLabel = a.platform || a.source_platform || '—';
                    const aiProvider = a.ai_provider || a.provider || '';
                    const aiModel = a.ai_model || a.model || '';
                    return (
                      <tr key={a.id} className="border-b border-white/5 hover:bg-white/[0.03]" data-testid={`mod-row-${a.id.slice(0, 8)}`}>
                        <td className="px-3 py-3 font-mono text-[11px] text-[#d4af37]">{a.reference_number || a.id?.slice(0, 12)}</td>
                        <td className="px-3 py-3">
                          <div className="text-sm text-white truncate max-w-[160px]">{a.customer_name || '—'}</div>
                          <div className="text-[10px] text-white/40 truncate max-w-[160px]">{a.customer_email || ''}</div>
                        </td>
                        <td className="px-3 py-3">
                          {platformLabel !== '—' ? (
                            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-md bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 font-semibold">
                              {platformLabel}
                            </span>
                          ) : <span className="text-[10px] text-white/30">—</span>}
                        </td>
                        <td className="px-3 py-3">
                          {/* iter-232b — Bot name/code column (replaces Service).
                             We prefer `ai_agent_code` when the conversation was
                             logged with a registered bot; fall back to the
                             service label so nothing renders blank. */}
                          {a.ai_agent_code ? (
                            <div className="flex flex-col gap-0.5">
                              <span className="text-[11px] font-mono text-[#d4af37] truncate max-w-[160px]" dir="ltr">{a.ai_agent_code}</span>
                              {a.ai_agent_name && (
                                <span className="text-[10px] text-white/60 truncate max-w-[160px]">{a.ai_agent_name}</span>
                              )}
                            </div>
                          ) : (
                            <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-md border ${svc.bg} ${svc.color} ${svc.border}`}>
                              <SvcIcon className="h-3 w-3" />
                              {language === 'ar' ? svc.labelAr : svc.labelEn}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          {/* iter-232b — Model used: just "Gemini" or "Claude". */}
                          {aiProvider ? (
                            <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-md border ${
                              aiProvider === 'anthropic'
                                ? 'bg-orange-500/10 border-orange-500/30 text-orange-300'
                                : 'bg-blue-500/10 border-blue-500/30 text-blue-300'
                            }`}>
                              {aiProvider === 'anthropic' ? 'Claude' : aiProvider === 'google' ? 'Gemini' : aiProvider}
                            </span>
                          ) : <span className="text-[10px] text-white/30">—</span>}
                        </td>
                        <td className="px-3 py-3 text-xs text-white/70 font-mono">{a.messages?.length || 0}</td>
                        <td className="px-3 py-3 text-[10px] text-white/50">{fmtDate(a.created_at, language)}</td>
                        <td className="px-3 py-3">
                          {a.review_status === 'reviewed' ? (
                            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-md bg-emerald-500/15 text-emerald-300 border border-emerald-500/40">
                              <CheckCircle2 className="h-3 w-3" />
                              {language === 'ar' ? 'تم المراجعة' : 'Reviewed'}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-md bg-amber-500/15 text-amber-300 border border-amber-500/40">
                              <Clock className="h-3 w-3" />
                              {language === 'ar' ? 'لم يتم المراجعة' : 'Not Reviewed'}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-3 justify-end">
                            <button
                              onClick={() => setViewing(a)}
                              className="text-sm font-semibold text-[#d4af37] hover:text-[#e5c449] underline underline-offset-2 decoration-[#d4af37]/40 hover:decoration-[#d4af37] transition-colors"
                              data-testid={`mod-view-${a.id.slice(0, 8)}`}
                            >
                              {language === 'ar' ? 'المراجعة' : 'Review'}
                            </button>
                            {reviewedOnly && (
                              <button
                                onClick={() => setDeleteTarget(a)}
                                className="p-1.5 rounded-md hover:bg-red-500/20 text-white/40 hover:text-red-300"
                                title={language === 'ar' ? 'حذف' : 'Delete'}
                                data-testid={`mod-delete-${a.id.slice(0, 8)}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <ArchiveDetailDialog
          open={!!viewing}
          onClose={() => setViewing(null)}
          archive={viewing}
          language={language}
          onReviewed={load}
        />

        <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
          <AlertDialogContent className="bg-[#09090b] border-white/10 text-white" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-red-300">
                <AlertTriangle className="h-5 w-5" />
                {language === 'ar' ? 'تأكيد الحذف' : 'Confirm Delete'}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-white/70 text-sm">
                {language === 'ar'
                  ? <>سيتم حذف المحادثة <span className="text-[#d4af37] font-mono">{deleteTarget?.reference_number || deleteTarget?.id?.slice(0, 12)}</span> نهائياً.</>
                  : <>Archive <span className="text-[#d4af37] font-mono">{deleteTarget?.reference_number || deleteTarget?.id?.slice(0, 12)}</span> will be deleted permanently.</>}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10" data-testid="mod-delete-cancel">
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </AlertDialogCancel>
              <AlertDialogAction onClick={doDelete} className="bg-red-500 hover:bg-red-600 text-white" data-testid="mod-delete-confirm">
                <Trash2 className="h-4 w-4 me-1.5" />
                {language === 'ar' ? 'حذف' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </section>
    </DashboardLayout>
  );
}
